import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import { api } from "@/lib/api";

/* ---------------- Settings (Zomato/Swiggy/phone) ---------------- */
const SettingsContext = createContext({ zomato_url: "", swiggy_url: "", company_phone: "", company_email: "", about: {} });

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({ zomato_url: "", swiggy_url: "", company_phone: "+91 98842 72560", company_email: "hello@mee2momos.com", whatsapp_number: "919884272560", hero_video_url: "", about: {} });
  useEffect(() => {
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);
  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
}
export const useSettings = () => useContext(SettingsContext);

export function WhatsAppButton() {
  const s = useSettings();
  const num = (s.whatsapp_number || "919884272560").replace(/\D/g, "");
  const href = `https://wa.me/${num}?text=${encodeURIComponent("Hi Mee2Momos, I'd like to know more about your catering / food services.")}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
      className="fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-xl transition-transform hover:scale-110 lg:bottom-6 lg:right-6"
      aria-label="Chat on WhatsApp">
      <i className="fa-brands fa-whatsapp text-3xl text-white" />
    </a>
  );
}

/* ---------------- Reveal on scroll ---------------- */
export function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ---------------- Section label (gold caption) ---------------- */
export function SectionLabel({ children, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-gold" />
      <span className="text-[11px] font-semibold uppercase tracking-editorial text-gold">{children}</span>
    </div>
  );
}

/* ---------------- Arch image ---------------- */
export function ArchImage({ src, alt, className = "", full = false, ratio = "aspect-[3/4]" }) {
  return (
    <div className={`overflow-hidden ${full ? "arch-full" : "arch-top"} border border-gold/25 shadow-2xl ${ratio} ${className}`}>
      <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105" />
    </div>
  );
}

/* ---------------- Pure Veg / Jain badge ---------------- */
export function DietBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-jain/40 bg-jain/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-jain">
      <span className="grid h-3 w-3 place-items-center rounded-full border border-jain">
        <span className="h-1.5 w-1.5 rounded-full bg-jain" />
      </span>
      Pure Veg &amp; Jain-Friendly
    </span>
  );
}

export function usePublicData(path, deps = []) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(path).then(({ data }) => setData(data)).catch(() => setData([]));
  }, [path]);
  useEffect(() => { load(); }, deps); // eslint-disable-line
  return data;
}
